import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdChevronLeft, MdChevronRight, MdClose } from 'react-icons/md';

export default function ListingImageGallery({
  images = [],
  title = 'Images',
  emptyText = 'No images uploaded yet.',
}) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const borderColor = useColorModeValue('blue.100', 'whiteAlpha.200');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const panelBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const cardBg = useColorModeValue('white', 'whiteAlpha.90');

  const validImages = Array.isArray(images) ? images : [];
  // Only images with a URL can be opened in the lightbox
  const lightboxImages = validImages.filter((img) => img.url);
  const isOpen = lightboxIndex !== null;
  const current = isOpen ? lightboxImages[lightboxIndex] : null;

  const handlePrev = useCallback(() => {
    setLightboxIndex((i) => (i <= 0 ? lightboxImages.length - 1 : i - 1));
  }, [lightboxImages.length]);

  const handleNext = useCallback(() => {
    setLightboxIndex((i) => (i >= lightboxImages.length - 1 ? 0 : i + 1));
  }, [lightboxImages.length]);

  const handleClose = useCallback(() => setLightboxIndex(null), []);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, handlePrev, handleNext, handleClose]);

  if (validImages.length === 0) {
    return (
      <Box
        border="1px solid"
        borderColor={borderColor}
        borderRadius="20px"
        bg={panelBg}
        px={{ base: 4, md: 5 }}
        py={4}
      >
        <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase" mb={1}>
          {title}
        </Text>
        <Text fontSize="sm" color={mutedText}>
          {emptyText}
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Box
        border="1px solid"
        borderColor={borderColor}
        borderRadius="20px"
        bg={panelBg}
        px={{ base: 4, md: 5 }}
        py={4}
      >
        <Text fontSize="xs" fontWeight="700" color={mutedText} textTransform="uppercase" mb={3}>
          {title} ({validImages.length})
        </Text>
        <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }} gap={3}>
          {validImages.map((image, index) => {
            const lbIndex = lightboxImages.indexOf(image);
            const clickable = lbIndex !== -1;
            return (
              <Box
                key={image.documentId || image.gcsPath || image.url || `${image.fileName}-${index}`}
                overflow="hidden"
                borderRadius="16px"
                border="1px solid"
                borderColor={borderColor}
                bg={cardBg}
                cursor={clickable ? 'pointer' : 'default'}
                onClick={clickable ? () => setLightboxIndex(lbIndex) : undefined}
                _hover={clickable ? { opacity: 0.85, transform: 'scale(1.02)', transition: 'all 0.15s' } : {}}
                transition="all 0.15s"
                role={clickable ? 'button' : undefined}
                aria-label={clickable ? `View ${image.fileName || `image ${index + 1}`} fullscreen` : undefined}
              >
                {image.url ? (
                  <Image
                    src={image.url}
                    alt={image.fileName || `Listing image ${index + 1}`}
                    w="100%"
                    h={{ base: '120px', md: '140px' }}
                    objectFit="cover"
                  />
                ) : (
                  <VStack justify="center" h={{ base: '120px', md: '140px' }} px={3}>
                    <Text fontSize="sm" color={mutedText} textAlign="center">
                      Preview unavailable
                    </Text>
                  </VStack>
                )}
                <Text px={3} py={2} fontSize="xs" color={mutedText} noOfLines={1}>
                  {image.fileName || `Image ${index + 1}`}
                </Text>
              </Box>
            );
          })}
        </Grid>
      </Box>

      {/* Lightbox */}
      <Modal isOpen={isOpen} onClose={handleClose} size="full" isCentered>
        <ModalOverlay bg="blackAlpha.900" />
        <ModalContent bg="transparent" boxShadow="none" m={0}>
          {/* Close button */}
          <IconButton
            icon={<MdClose size={24} />}
            aria-label="Close"
            position="fixed"
            top={4}
            right={4}
            zIndex={10}
            colorScheme="whiteAlpha"
            borderRadius="full"
            size="lg"
            onClick={handleClose}
          />

          {/* Counter */}
          {lightboxImages.length > 1 && (
            <Text
              position="fixed"
              top={5}
              left="50%"
              transform="translateX(-50%)"
              zIndex={10}
              color="white"
              fontSize="sm"
              fontWeight="700"
              bg="blackAlpha.600"
              px={3}
              py={1}
              borderRadius="full"
            >
              {lightboxIndex + 1} / {lightboxImages.length}
            </Text>
          )}

          {/* Image */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="100vw"
            h="100vh"
            px={{ base: 12, md: 20 }}
            onClick={handleClose}
          >
            {current?.url && (
              <Image
                src={current.url}
                alt={current.fileName || 'Image'}
                maxH="90vh"
                maxW="100%"
                objectFit="contain"
                borderRadius="lg"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </Box>

          {/* Prev / Next */}
          {lightboxImages.length > 1 && (
            <HStack
              position="fixed"
              bottom={6}
              left="50%"
              transform="translateX(-50%)"
              zIndex={10}
              spacing={4}
            >
              <IconButton
                icon={<MdChevronLeft size={28} />}
                aria-label="Previous image"
                colorScheme="whiteAlpha"
                borderRadius="full"
                size="lg"
                onClick={(e) => { e.stopPropagation(); handlePrev(); }}
              />
              <IconButton
                icon={<MdChevronRight size={28} />}
                aria-label="Next image"
                colorScheme="whiteAlpha"
                borderRadius="full"
                size="lg"
                onClick={(e) => { e.stopPropagation(); handleNext(); }}
              />
            </HStack>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
